import { Box, Button, Divider, useToast } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { LeadTypes } from "@leadpro/forms";
import {
  ActionPromptContainer,
  ApiMessageStack,
  FormControlError,
  FormControlMeta,
  FormControlV2,
  Message,
  MultiSelectInput,
} from "components";
import { AlertStatusEnum } from "enums/alert-status.enum";
import { DEFAULT_SUCCESS_TOAST_OPTIONS } from "constants/default-toast-options";
import {
  ALL_LEAD_TYPES,
  LEAD_TYPE_OPTIONS,
  TLeadTypeSelection,
} from "constants/lead-type-options";
import { FormControlsTypeEnum } from "enums/form-controls-type.enum";
import { FC, useCallback, useMemo, useState } from "react";
import { useController, useForm } from "react-hook-form";
import {
  TSelectOption,
  TSelectOptionValue,
  TSelectValue,
} from "types/select-input.type";
import {
  ALL_SOURCE_OFFICES,
  LEAD_INACTIVITY_TYPE_LABELS,
  TSourceOfficeSelection,
  UNASSIGNED_SOURCE_OFFICE,
} from "constants/lead-inactivity";
import {
  TAccountLeadInactivity,
  TAccountLeadInactivityPayload,
} from "types/account-lead-inactivity.type";
import { LeadInactivityTypeEnum } from "enums/lead-inactivity-type.enum";
import { leadInactivityValidationSchema } from "utils/validation-schemas/account-lead-inactivity.validation";
import { getLeadTypeLabel } from "utils/lead-type.utils";
import { filterOptionsByLabel } from "utils/select.utils";
import { applyExclusiveOption } from "utils/exclusive-option.utils";

type TFieldValues = {
  enabled: boolean;
  delay: number | null;
  triggerWithinOfficeHours: boolean;
  leadTypes: TLeadTypeSelection[];
  sourceOfficeIds: TSourceOfficeSelection[];
  type: LeadInactivityTypeEnum | null;
  officeId: number | null;
};

type TValidatedFieldValues = TFieldValues & {
  delay: number;
  type: LeadInactivityTypeEnum;
  officeId: number;
};

const ACTION_OPTIONS = Object.values(LeadInactivityTypeEnum).map(value => ({
  value,
  label: LEAD_INACTIVITY_TYPE_LABELS[value],
}));

const toLeadTypesPayload = (selection: TLeadTypeSelection[]): LeadTypes[] =>
  selection.includes(ALL_LEAD_TYPES) ? [] : (selection as LeadTypes[]);

const toSourceOfficesPayload = (
  selection: TSourceOfficeSelection[]
): number[] =>
  selection.includes(ALL_SOURCE_OFFICES) ? [] : (selection as number[]);

const toPayload = (
  values: TValidatedFieldValues
): TAccountLeadInactivityPayload => {
  const {
    enabled,
    delay,
    triggerWithinOfficeHours,
    leadTypes,
    sourceOfficeIds,
    type,
    officeId,
  } = values;

  switch (type) {
    case LeadInactivityTypeEnum.REROUTE_TO_OFFICE:
      return {
        enabled,
        delay,
        triggerWithinOfficeHours,
        type,
        leadTypes: toLeadTypesPayload(leadTypes),
        sourceOfficeIds: toSourceOfficesPayload(sourceOfficeIds),
        config: { officeId },
      };
  }
};

const withOptionsForMissingValues = <T extends TSelectOptionValue>(
  options: TSelectOption<T>[],
  selectedValues: T[],
  getMissingValueLabel: (value: T) => string
): TSelectOption<T>[] => {
  const missingValues = selectedValues.filter(
    value => !options.some(option => option.value === value)
  );
  if (!missingValues.length) return options;
  return [
    ...options,
    ...missingValues.map(value => ({
      value,
      label: getMissingValueLabel(value),
    })),
  ];
};

interface IProps {
  closePrompt: () => void;
  officeOptions: TSelectOption<number>[];
  getOfficeName: (officeId: number) => string;
  rule?: TAccountLeadInactivity;
  onSave: (payload: TAccountLeadInactivityPayload) => Promise<void>;
}

export const LeadInactivityRuleActionPrompt: FC<IProps> = ({
  closePrompt,
  officeOptions,
  getOfficeName,
  rule,
  onSave,
}) => {
  const toast = useToast();
  const [saveError, setSaveError] = useState<string | string[] | null>(null);

  const initialValues: TFieldValues = useMemo(
    () => ({
      enabled: rule?.enabled ?? true,
      delay: rule?.delay ?? null,
      triggerWithinOfficeHours: rule?.triggerWithinOfficeHours ?? false,
      leadTypes: rule?.leadTypes?.length
        ? [...rule.leadTypes]
        : [ALL_LEAD_TYPES],
      sourceOfficeIds: rule?.sourceOfficeIds?.length
        ? [...rule.sourceOfficeIds]
        : [ALL_SOURCE_OFFICES],
      type: rule?.type ?? LeadInactivityTypeEnum.REROUTE_TO_OFFICE,
      officeId: rule?.config?.officeId ?? null,
    }),
    [rule]
  );

  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting },
  } = useForm<TFieldValues>({
    defaultValues: initialValues,
    mode: "onSubmit",
    resolver: yupResolver(leadInactivityValidationSchema),
  });

  const showOfficeInput =
    watch("type") === LeadInactivityTypeEnum.REROUTE_TO_OFFICE;

  const triggerWithinOfficeHours = watch("triggerWithinOfficeHours");

  const {
    field: { value: leadTypes, onChange: onLeadTypesChange },
    fieldState: { error: leadTypesError },
  } = useController({ name: "leadTypes", control });

  const {
    field: { value: sourceOfficeIds, onChange: onSourceOfficesChange },
    fieldState: { error: sourceOfficeIdsError },
  } = useController({ name: "sourceOfficeIds", control });

  const handleLeadTypesChange = useCallback(
    (next: TSelectValue<TLeadTypeSelection>[]) =>
      onLeadTypesChange(
        applyExclusiveOption(
          next as TLeadTypeSelection[],
          leadTypes,
          ALL_LEAD_TYPES
        )
      ),
    [leadTypes, onLeadTypesChange]
  );

  const handleSourceOfficesChange = useCallback(
    (next: TSelectValue<TSourceOfficeSelection>[]) =>
      onSourceOfficesChange(
        applyExclusiveOption(
          next as TSourceOfficeSelection[],
          sourceOfficeIds,
          ALL_SOURCE_OFFICES
        )
      ),
    [sourceOfficeIds, onSourceOfficesChange]
  );

  const officeSelectOptions = useMemo(
    () =>
      withOptionsForMissingValues(
        officeOptions,
        rule?.config?.officeId ? [rule.config.officeId] : [],
        officeId => `${getOfficeName(officeId)} (unavailable)`
      ),
    [officeOptions, rule, getOfficeName]
  );

  const leadTypeSelectOptions: TSelectOption<
    TLeadTypeSelection
  >[] = useMemo(
    () => [
      { value: ALL_LEAD_TYPES, label: "All" },
      ...withOptionsForMissingValues(
        LEAD_TYPE_OPTIONS,
        rule?.leadTypes || [],
        leadType => getLeadTypeLabel(leadType, true)
      ),
    ],
    [rule]
  );

  const sourceOfficeSelectOptions: TSelectOption<
    TSourceOfficeSelection
  >[] = useMemo(
    () => [
      { value: ALL_SOURCE_OFFICES, label: "All" },
      { value: UNASSIGNED_SOURCE_OFFICE, label: "Unassigned" },
      ...withOptionsForMissingValues(
        officeOptions,
        (rule?.sourceOfficeIds || []).filter(
          officeId => officeId !== UNASSIGNED_SOURCE_OFFICE
        ),
        officeId => `${getOfficeName(officeId)} (unavailable)`
      ),
    ],
    [officeOptions, rule, getOfficeName]
  );

  const onSubmit = useCallback(
    async (values: TFieldValues) => {
      setSaveError(null);

      const payload = toPayload(values as TValidatedFieldValues);

      try {
        await onSave(payload);
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack
              messageStack={
                rule ? "Redirect rule updated." : "Redirect rule added."
              }
            />
          ),
        });
        closePrompt();
      } catch (e) {
        setSaveError(e.message);
      }
    },
    [onSave, toast, closePrompt, rule]
  );

  return (
    <ActionPromptContainer
      header={rule ? "Edit redirect rule" : "Add redirect rule"}
      body={
        <Box pt={5}>
          <form onSubmit={handleSubmit(onSubmit, () => setSaveError(null))}>
            {!!saveError && (
              <Message
                status={AlertStatusEnum.ERROR}
                mb={5}
                borderRadius={"md"}
              >
                <ApiMessageStack messageStack={saveError} />
              </Message>
            )}
            <Box mb={5}>
              <Box flexShrink={0}>
                <FormControlMeta label={"Lead types"} />
              </Box>
              <Box>
                <MultiSelectInput<TLeadTypeSelection>
                  value={leadTypes}
                  options={leadTypeSelectOptions}
                  onChange={handleLeadTypesChange}
                  placeholder={"Select lead types"}
                />
                {!!leadTypesError && (
                  <FormControlError error={leadTypesError} />
                )}
              </Box>
            </Box>
            <Box mb={5}>
              <Box flexShrink={0}>
                <FormControlMeta label={"Source offices"} />
              </Box>
              <Box>
                <MultiSelectInput<TSourceOfficeSelection>
                  value={sourceOfficeIds}
                  options={sourceOfficeSelectOptions}
                  onChange={handleSourceOfficesChange}
                  placeholder={"Select source offices"}
                  filterPlaceholder={"Search offices"}
                  filterFn={filterOptionsByLabel}
                />
                {!!sourceOfficeIdsError && (
                  <FormControlError error={sourceOfficeIdsError} />
                )}
              </Box>
            </Box>
            <Box mb={5}>
              <FormControlV2<TFieldValues>
                name={"delay"}
                control={control}
                label={"Time limit (minutes)"}
                type={FormControlsTypeEnum.NUMBER}
              />
            </Box>
            <Box mb={5}>
              <FormControlV2<TFieldValues>
                name={"triggerWithinOfficeHours"}
                control={control}
                label={"Enable during office working hours"}
                type={FormControlsTypeEnum.SWITCH}
              />
              {triggerWithinOfficeHours && (
                <Message
                  status={AlertStatusEnum.INFO}
                  mt={2}
                  borderRadius={"md"}
                >
                  Leads arriving out of hours start waiting when the office
                  opens. Offices use their own working hours where set,
                  otherwise the account&apos;s.
                </Message>
              )}
            </Box>
            <Box mb={5}>
              <FormControlV2<TFieldValues>
                name={"type"}
                control={control}
                label={"Action"}
                type={FormControlsTypeEnum.SINGLE_SELECT}
                additionalProps={{ options: ACTION_OPTIONS }}
              />
            </Box>
            {showOfficeInput && (
              <Box mb={5}>
                <FormControlV2<TFieldValues>
                  name={"officeId"}
                  control={control}
                  label={"Redirect to"}
                  type={FormControlsTypeEnum.SINGLE_SELECT}
                  additionalProps={{
                    options: officeSelectOptions,
                    placeholder: "Select an office",
                    filterPlaceholder: "Search offices",
                    filterFn: filterOptionsByLabel,
                  }}
                />
              </Box>
            )}
            <Box mb={5}>
              <FormControlV2<TFieldValues>
                name={"enabled"}
                control={control}
                label={"Rule is enabled"}
                type={FormControlsTypeEnum.SWITCH}
              />
            </Box>
            <Divider mb={4} />
            <Box
              width={"100%"}
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
            >
              <Button
                type={"submit"}
                colorScheme={"blue"}
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
              >
                Save
              </Button>
            </Box>
          </form>
        </Box>
      }
    />
  );
};
